import moment from 'moment-timezone';
import { useSettings } from '../contexts/SettingsContext';

/**
 * Comprehensive timezone utilities for the pos system
 */

/**
 * Get list of all timezones with their display names
 */
export const getTimezoneList = () => {
  const timezones = moment.tz.names();
  
  return timezones.map(tz => {
    const zone = moment.tz.zone(tz);
    if (!zone) return null;
    
    const now = moment();
    const offset = now.tz(tz).format('Z');
    const abbreviation = now.tz(tz).format('z');
    
    return {
      value: tz,
      label: `${tz.replace(/_/g, ' ')} (${abbreviation}, ${offset})`,
      region: tz.split('/')[0],
      city: tz.split('/').slice(1).join('/').replace(/_/g, ' ')
    };
  }).filter(Boolean).sort((a, b) => {
    if (a!.region === b!.region) {
      return a!.city.localeCompare(b!.city);
    }
    return a!.region.localeCompare(b!.region);
  }) as Array<{
    value: string;
    label: string;
    region: string;
    city: string;
  }>;
};

/**
 * Get grouped timezones by region
 */
export const getGroupedTimezones = () => {
  const timezones = getTimezoneList();
  const grouped: { [region: string]: Array<{ value: string; label: string; city: string }> } = {};
  
  timezones.forEach(tz => {
    if (!grouped[tz.region]) {
      grouped[tz.region] = [];
    }
    grouped[tz.region].push({
      value: tz.value,
      label: tz.label,
      city: tz.city
    });
  });
  
  return grouped;
};

/**
 * Get popular/common timezones
 */
export const getPopularTimezones = () => {
  const popular = [
    'Asia/Karachi',      // Pakistan
    'UTC',               // UTC
    'America/New_York',  // US Eastern
    'America/Los_Angeles', // US Pacific
    'Europe/London',     // UK
    'Europe/Paris',      // Central Europe
    'Asia/Dubai',        // UAE
    'Asia/Kolkata',      // India
    'Asia/Shanghai',     // China
    'Asia/Tokyo',        // Japan
    'Australia/Sydney',  // Australia
    'Africa/Cairo',      // Egypt
  ];
  
  const allTimezones = getTimezoneList();
  return popular.map(tz => 
    allTimezones.find(t => t.value === tz)
  ).filter(Boolean) as Array<{
    value: string;
    label: string;
    region: string;
    city: string;
  }>;
};

/**
 * Format date in user's selected timezone
 */
export const formatDateInTimezone = (
  date: string | Date | moment.Moment,
  timezone: string,
  format: string = 'YYYY-MM-DD HH:mm:ss'
): string => {
  return moment(date).tz(timezone).format(format);
};

/**
 * Convert date from one timezone to another
 */
export const convertTimezone = (
  date: string | Date | moment.Moment,
  fromTimezone: string,
  toTimezone: string,
  format?: string
): string | moment.Moment => {
  const converted = moment.tz(date, fromTimezone).tz(toTimezone);
  return format ? converted.format(format) : converted;
};

/**
 * Get current date/time in user's timezone
 */
export const getNowInTimezone = (timezone: string): moment.Moment => {
  return moment().tz(timezone);
};

/**
 * Format relative time in user's timezone
 */
export const formatRelativeTime = (
  date: string | Date | moment.Moment,
  timezone: string
): string => {
  return moment(date).tz(timezone).fromNow();
};

/**
 * Custom hook for timezone operations
 */
export const useTimezone = () => {
  const { getSetting } = useSettings();
  
  const getUserTimezone = (): string => {
    return getSetting('timezone') || 'UTC';
  };
  
  const formatDate = (
    date: string | Date | moment.Moment,
    format: string = 'YYYY-MM-DD HH:mm:ss'
  ): string => {
    return formatDateInTimezone(date, getUserTimezone(), format);
  };
  
  const formatDateTime = (date: string | Date | moment.Moment): string => {
    return formatDate(date, 'MMM DD, YYYY h:mm A');
  };
  
  const formatTime = (date: string | Date | moment.Moment): string => {
    return formatDate(date, 'h:mm A');
  };
  
  const formatDateOnly = (date: string | Date | moment.Moment): string => {
    return formatDate(date, 'MMM DD, YYYY');
  };
  
  const getRelativeTime = (date: string | Date | moment.Moment): string => {
    return formatRelativeTime(date, getUserTimezone());
  };
  
  const getCurrentDateTime = (): moment.Moment => {
    return getNowInTimezone(getUserTimezone());
  };
  
  return {
    timezone: getUserTimezone(),
    formatDate,
    formatDateTime,
    formatTime,
    formatDateOnly,
    getRelativeTime,
    getCurrentDateTime,
    convertToUserTimezone: (date: string | Date | moment.Moment) => 
      moment(date).tz(getUserTimezone()),
  };
};

/**
 * Validate timezone string
 */
export const isValidTimezone = (timezone: string): boolean => {
  return moment.tz.names().includes(timezone);
};

/**
 * Get timezone info
 */
export const getTimezoneInfo = (timezone: string) => {
  if (!isValidTimezone(timezone)) {
    return null;
  }
  
  const now = moment().tz(timezone);
  return {
    name: timezone,
    offset: now.format('Z'),
    abbreviation: now.format('z'),
    isDST: now.isDST(),
    utcOffset: now.utcOffset(),
  };
};